/**
 * useOutbox — reactive hook surfacing the outbox to React components.
 *
 *   const { items, stats, refresh } = useOutbox({ companyId, businessId });
 *
 * Subscribes to the same change events the sync engine emits so UI
 * updates within milliseconds of a status transition.
 */
import { useCallback, useEffect, useState } from "react";
import {
  listByScope,
  getStats,
  onOutboxChanged,
  type OutboxItem,
  type OutboxStats,
} from "@/offline/outbox";

interface UseOutboxOpts {
  /** Optional status filter — e.g. only show pending+failed. */
  statuses?: OutboxItem["status"][];
}

export function useOutbox(
  scope: { companyId: string | null; businessId: string | null },
  opts: UseOutboxOpts = {},
) {
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [stats, setStats] = useState<OutboxStats | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!scope.companyId || !scope.businessId) {
      setItems([]);
      setStats(null);
      setLoading(false);
      return;
    }
    try {
      const [list, s] = await Promise.all([
        listByScope(scope.companyId, scope.businessId, {
          status: opts.statuses,
        }),
        getStats(scope.companyId, scope.businessId),
      ]);
      setItems(list);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }, [scope.companyId, scope.businessId, opts.statuses]);

  useEffect(() => {
    refresh();
    const unsub = onOutboxChanged(() => refresh());
    return unsub;
  }, [refresh]);

  return { items, stats, loading, refresh };
}
